import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import app from "../../firebase";
import { clearEnrolledSession } from "../../auth/roleSession";
import {
  formatCalendarDate,
  getCalendarRangeStatus,
  getAnnouncementSchoolYear,
  isCalendarDateInRange,
  toCalendarDateKey,
  useCalendarClock
} from "../../utils/announcementDates";

export default function PreRegistration({ student: propStudent }) {
  const todayKey = useCalendarClock();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [student, setStudent] = useState(propStudent || state?.student || null);
  const [step, setStep] = useState("CLEARANCE");
  const [loading, setLoading] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [announcementData, setAnnouncementData] = useState(null);

  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1100;
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  const fullName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "Enrolled Student";
  const profilePicUrl = student?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Enrolled")}`;


  const formatDate = (dateStr) => {
    return formatCalendarDate(dateStr);
  };

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      navigate("/enrolled/signin");
      return;
    }
  };

  const buildEnrolledWindowInfo = (data = {}) => {
    const firstStart = data.firstSemEnrollmentStart
      ? data.firstSemEnrollmentStart
      : (data.firstSemStart || null);
    const firstEnd = data.firstSemEnrollmentEnd
      ? data.firstSemEnrollmentEnd
      : (data.firstSemEnd || null);
    const secondStart = data.secondSemEnrollmentStart
      ? data.secondSemEnrollmentStart
      : (data.secondSemStart || null);
    const secondEnd = data.secondSemEnrollmentEnd
      ? data.secondSemEnrollmentEnd
      : (data.secondSemEnd || null);

    const fallbackYear = data.schoolYearStart && data.schoolYearEnd ? `${data.schoolYearStart}-${data.schoolYearEnd}` : "";
    const firstYear = data.firstSemYearStart && data.firstSemYearEnd ? `${data.firstSemYearStart}-${data.firstSemYearEnd}` : fallbackYear;
    const secondYear = data.secondSemYearStart && data.secondSemYearEnd ? `${data.secondSemYearStart}-${data.secondSemYearEnd}` : fallbackYear;

    const windows = [
      { key: "first", semester: "First Semester", year: firstYear, start: firstStart, end: firstEnd },
      { key: "second", semester: "Second Semester", year: secondYear, start: secondStart, end: secondEnd }
    ].filter(w => toCalendarDateKey(w.start) && toCalendarDateKey(w.end));

    const active = windows.find(w => isCalendarDateInRange(w.start, w.end, todayKey)) || null;
    const next = windows
      .filter(w => todayKey < toCalendarDateKey(w.start))
      .sort((a, b) => toCalendarDateKey(a.start).localeCompare(toCalendarDateKey(b.start)))[0] || null;

    return { active, next };
  };

  const buildSemesterInfo = (data = {}) => {
    const firstStart = data.firstSemStart || null;
    const firstEnd = data.firstSemEnd || null;
    const secondStart = data.secondSemStart || null;
    const secondEnd = data.secondSemEnd || null;

    const fallbackYear = data.schoolYearStart && data.schoolYearEnd ? `${data.schoolYearStart}-${data.schoolYearEnd}` : "";
    const firstYear = data.firstSemYearStart && data.firstSemYearEnd ? `${data.firstSemYearStart}-${data.firstSemYearEnd}` : fallbackYear;
    const secondYear = data.secondSemYearStart && data.secondSemYearEnd ? `${data.secondSemYearStart}-${data.secondSemYearEnd}` : fallbackYear;

    const windows = [
      {
        key: "first",
        semester: "First Semester",
        year: firstYear,
        start: firstStart,
        end: firstEnd,
        enrollStart: data.firstSemEnrollmentStart || null,
        enrollEnd: data.firstSemEnrollmentEnd || null
      },
      {
        key: "second",
        semester: "Second Semester",
        year: secondYear,
        start: secondStart,
        end: secondEnd,
        enrollStart: data.secondSemEnrollmentStart || null,
        enrollEnd: data.secondSemEnrollmentEnd || null
      }
    ].filter(w => toCalendarDateKey(w.start) && toCalendarDateKey(w.end));

    const ongoing = windows.find(w => isCalendarDateInRange(w.start, w.end, todayKey)) || null;
    const waiting = windows
      .filter(w => todayKey < toCalendarDateKey(w.start))
      .sort((a, b) => toCalendarDateKey(a.start).localeCompare(toCalendarDateKey(b.start)))[0] || null;

    return { ongoing, waiting };
  };

  // Load student from Firebase
  useEffect(() => {
    if (student?.id) {
      const db = getFirestore(app);
      const studentRef = doc(db, "students", student.id);
      const unsubscribe = onSnapshot(studentRef, (snap) => {
        if (snap.exists()) {
          setStudent({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [student?.id]);

  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "announcement");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAnnouncementData(data);
      }
    });
    return () => unsubscribe();
  }, []);

  // Check requirements clearance
  const checkRequirementsClearance = () => {
    if (!student) return { isCleared: false, missingReqs: [] };

    const requirements = student.requirements || {};
    const missingReqs = [];

    if (!requirements.pic) missingReqs.push("2x2 Picture");
    if (!requirements.cert) missingReqs.push("Birth/Marriage Certificate");
    if (!requirements.id) missingReqs.push("Latest School/Government ID");
    if (!requirements.grades) missingReqs.push("Copy of Grades");

    return {
      isCleared: missingReqs.length === 0,
      missingReqs
    };
  };

  const getTermKeyFromAnnouncement = () => {
    if (!announcementData) return "";
    const firstSemStatus = getSemesterStatus(announcementData.firstSemStart, announcementData.firstSemEnd);
    const secondSemStatus = getSemesterStatus(announcementData.secondSemStart, announcementData.secondSemEnd);

    if (firstSemStatus === "Ongoing") {
      const year = getAnnouncementSchoolYear(announcementData, "first");
      return year ? `${year}_First Semester` : "";
    }
    if (secondSemStatus === "Ongoing") {
      const year = getAnnouncementSchoolYear(announcementData, "second");
      return year ? `${year}_Second Semester` : "";
    }
    return "";
  };

  const getLatestTermKey = () => {
    const sources = [
      student?.termSchedules,
      student?.miscellaneousFeesHistory,
      student?.payments,
      student?.installmentPayments
    ].filter(Boolean);

    for (const src of sources) {
      if (src && typeof src === "object" && !Array.isArray(src)) {
        const keys = Object.keys(src);
        if (keys.length > 0) return keys.sort().pop() || "";
      }
    }
    return "";
  };

  const getBalanceFromPayments = (termKey) => {
    const payments = student?.payments;
    if (payments) {
      if (Array.isArray(payments)) {
        const last = payments[payments.length - 1];
        if (last && typeof last.balance === "number") return last.balance;
      } else if (termKey && Array.isArray(payments[termKey])) {
        const termPayments = payments[termKey];
        const last = termPayments[termPayments.length - 1];
        if (last && typeof last.balance === "number") return last.balance;
      }
    }

    const installments = student?.installmentPayments;
    if (installments && termKey && Array.isArray(installments[termKey])) {
      const termInstallments = installments[termKey];
      const latestSet = termInstallments[termInstallments.length - 1] || {};
      const order = ["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"];
      for (let i = order.length - 1; i >= 0; i -= 1) {
        const payment = latestSet[order[i]];
        if (payment && typeof payment.balance === "number") return payment.balance;
      }
    }

    return null;
  };

  // Calculate student balance from payment history for the current term
  const calculateBalance = () => {
    if (!student) return 0;

    const termKey = getTermKeyFromAnnouncement() || getLatestTermKey();
    const balanceFromPayments = getBalanceFromPayments(termKey);
    if (typeof balanceFromPayments === "number") return Math.max(0, balanceFromPayments);

    const miscHistory = student?.miscellaneousFeesHistory;
    if (termKey && miscHistory && Array.isArray(miscHistory[termKey]) && miscHistory[termKey].length > 0) {
      const snapshot = miscHistory[termKey][miscHistory[termKey].length - 1] || {};
      const totalAmount = parseFloat(snapshot.totalAmount) || 0;
      return Math.max(0, totalAmount);
    }

    return 0;
  };

  const requirementsClearance = checkRequirementsClearance();
  const studentBalance = calculateBalance();
  const isFinanceCleared = studentBalance <= 0;
  const isFullyCleared = requirementsClearance.isCleared && isFinanceCleared;

  const enrolledWindowInfo = announcementData
    ? buildEnrolledWindowInfo(announcementData)
    : { active: null, next: null };
  const isEnrolledWindowActive = !!enrolledWindowInfo.active;
  const windowForMessage = enrolledWindowInfo.active || enrolledWindowInfo.next;
  const semesterInfo = announcementData ? buildSemesterInfo(announcementData) : { ongoing: null, waiting: null };
  const ongoingSemester = semesterInfo.ongoing;
  const waitingSemester = semesterInfo.waiting;

  // Helper to get semester status (with fallback if Firebase status not available)
  const getSemesterStatus = (startDate, endDate) => {
    return getCalendarRangeStatus(startDate, endDate, todayKey);
  };

  const firstSemStatus = getSemesterStatus(announcementData?.firstSemStart, announcementData?.firstSemEnd);
  const secondSemStatus = getSemesterStatus(announcementData?.secondSemStart, announcementData?.secondSemEnd);
  const isCurrentTermOngoing = firstSemStatus === "Ongoing" || secondSemStatus === "Ongoing";
  const canPreRegister = !isCurrentTermOngoing;
  const isEnrolledWindowOpen = isEnrolledWindowActive && canPreRegister;

  // Helper to check if enrollment window is open
  const isEnrollmentWindowOpenDates = (startDate, endDate) => {
    return isCalendarDateInRange(startDate, endDate, todayKey);
  };

  // Build closed enrollment message using Firebase status fields (with fallback)
  const getEnrollmentClosedMessage = () => {
    if (!announcementData) return null;

    const firstSemStatus = getSemesterStatus(announcementData.firstSemStart, announcementData.firstSemEnd);
    const secondSemStatus = getSemesterStatus(announcementData.secondSemStart, announcementData.secondSemEnd);
    const firstEnrollmentOpen = isEnrollmentWindowOpenDates(announcementData.firstSemEnrollmentStart, announcementData.firstSemEnrollmentEnd);
    const secondEnrollmentOpen = isEnrollmentWindowOpenDates(announcementData.secondSemEnrollmentStart, announcementData.secondSemEnrollmentEnd);

    let messages = [];

    // Check if there's an ongoing semester with closed enrollment
    if (firstSemStatus === "Ongoing" && !firstEnrollmentOpen) {
      const year = announcementData.firstSemYearStart && announcementData.firstSemYearEnd 
        ? `${announcementData.firstSemYearStart} - ${announcementData.firstSemYearEnd}`
        : (announcementData.schoolYearStart && announcementData.schoolYearEnd ? `${announcementData.schoolYearStart} - ${announcementData.schoolYearEnd}` : "—");
      messages.push(<>Enrollment for <b>AY {year}</b> <b>First Semester</b> is already closed.</>);
    }
    if (secondSemStatus === "Ongoing" && !secondEnrollmentOpen) {
      const year = announcementData.secondSemYearStart && announcementData.secondSemYearEnd 
        ? `${announcementData.secondSemYearStart} - ${announcementData.secondSemYearEnd}`
        : (announcementData.schoolYearStart && announcementData.schoolYearEnd ? `${announcementData.schoolYearStart} - ${announcementData.schoolYearEnd}` : "—");
      messages.push(<>Enrollment for <b>AY {year}</b> <b>Second Semester</b> is already closed.</>);
    }

    // Check if there's a waiting semester (with enrollment dates)
    if (firstSemStatus === "Waiting" && announcementData.firstSemEnrollmentStart && announcementData.firstSemEnrollmentEnd) {
      const year = announcementData.firstSemYearStart && announcementData.firstSemYearEnd 
        ? `${announcementData.firstSemYearStart} - ${announcementData.firstSemYearEnd}`
        : (announcementData.schoolYearStart && announcementData.schoolYearEnd ? `${announcementData.schoolYearStart} - ${announcementData.schoolYearEnd}` : "—");
      const enrollStart = formatDate(announcementData.firstSemEnrollmentStart);
      const enrollEnd = formatDate(announcementData.firstSemEnrollmentEnd);
      if (enrollStart && enrollEnd) {
        messages.push(<>The Enrollment for <b>AY {year}</b> <b>First Semester</b> will be open from <b>{enrollStart}</b> to <b>{enrollEnd}</b>.</>);
      }
    }
    if (secondSemStatus === "Waiting" && announcementData.secondSemEnrollmentStart && announcementData.secondSemEnrollmentEnd) {
      const year = announcementData.secondSemYearStart && announcementData.secondSemYearEnd 
        ? `${announcementData.secondSemYearStart} - ${announcementData.secondSemYearEnd}`
        : (announcementData.schoolYearStart && announcementData.schoolYearEnd ? `${announcementData.schoolYearStart} - ${announcementData.schoolYearEnd}` : "—");
      const enrollStart = formatDate(announcementData.secondSemEnrollmentStart);
      const enrollEnd = formatDate(announcementData.secondSemEnrollmentEnd);
      if (enrollStart && enrollEnd) {
        messages.push(<>The Enrollment for <b>AY {year}</b> <b>Second Semester</b> will be open from <b>{enrollStart}</b> to <b>{enrollEnd}</b>.</>);
      }
    }

    if (messages.length > 0) {
      return {
        main: "Enrollment is currently closed.",
        details: messages
      };
    }

    return null;
  };

  const enrollmentClosedMsg = getEnrollmentClosedMessage();

  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      {/* Sidebar */}
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = window.location.pathname.startsWith(n.path);
            return (
              <div
                key={n.key}
                onClick={() => navigate(n.path, { state: { student } })}
                style={{
                  padding: "14px 24px",
                  margin: "4px 16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#ff6f2e" : "transparent",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s",
                  boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
                }}
              >
                {n.label}
              </div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        {/* Header */}
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999, position: "relative" }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Pre-Registration</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 180, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto", background: "#efefef", marginTop: 0 }} data-scroll-container>
          <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto" }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 48, color: "#666" }}>Loading pre-registration data...</div>
            ) : (
              <>
                {!isEnrolledWindowOpen ? (
                  <div style={{ background: "#fff3cd", border: "1px solid #ffeeba", borderRadius: 8, padding: 16, marginBottom: 16, color: "#856404" }}>
                    {!canPreRegister ? (
                      <>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>Pre-registration is not available while the current semester is ongoing.</div>
                        <div>Please finish the current term before pre-registering for the next term.</div>
                      </>
                    ) : enrollmentClosedMsg ? (
                      <>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>{enrollmentClosedMsg.main}</div>
                        {enrollmentClosedMsg.details && enrollmentClosedMsg.details.map((detail, idx) => (
                          <div key={idx} style={{ marginTop: idx > 0 ? 16 : 0 }}>{detail}</div>
                        ))}
                      </>
                    ) : (
                      <div style={{ fontWeight: 700 }}>Enrollment is currently closed.</div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Step Indicator */}
                    <div style={{ display: "flex", gap: 24, marginBottom: 32 }}>
                      {["CLEARANCE", "COURSE LINE-UP", "PAYMENT OF FEES"].map((stepName, idx) => (
                        <div
                          key={stepName}
                          onClick={() => {
                            if (stepName === "CLEARANCE") setStep(stepName);
                            else if (stepName === "COURSE LINE-UP" && isFullyCleared && isEnrolledWindowOpen) {
                              navigate("/enrolled/course-lineup", { state: { student } });
                            }
                            else if (stepName === "PAYMENT OF FEES" && isFullyCleared && isEnrolledWindowOpen) {
                              navigate("/enrolled/payment-of-fees", { state: { student } });
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: "16px 24px",
                            background: step === stepName ? "#ff6f2e" : "#fff",
                            color: step === stepName ? "#fff" : "#0f5f3b",
                            borderRadius: 8,
                            fontWeight: 700,
                            textAlign: "center",
                            cursor: (stepName === "CLEARANCE" || (isFullyCleared && isEnrolledWindowOpen)) ? "pointer" : "not-allowed",
                            opacity: (stepName === "CLEARANCE" || (isFullyCleared && isEnrolledWindowOpen)) ? 1 : 0.5,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                          }}
                        >
                          {idx + 1}. {stepName}
                        </div>
                      ))}
                    </div>

                  {/* CLEARANCE STEP */}
                  {step === "CLEARANCE" && (
                  <div style={{ background: "#fff", borderRadius: 12, padding: "48px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                    <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 32, color: "#0f5f3b", textAlign: "center" }}>
                      CLEARANCE REQUIREMENTS
                    </div>

                    {/* Requirements Section */}
                    <div style={{ marginBottom: 40 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: "#0f5f3b", borderBottom: "3px solid #0f5f3b", paddingBottom: 8 }}>
                        Requirements Checklist
                      </div>

                      {requirementsClearance.isCleared ? (
                        <div style={{ background: "#d4edda", border: "2px solid #28a745", borderRadius: 8, padding: 20, color: "#155601", marginBottom: 24 }}>
                          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>✓ All Requirements Submitted</div>
                          <div>You have successfully submitted all required documents:</div>
                          <ul style={{ marginTop: 12, marginLeft: 24, lineHeight: 1.8 }}>
                            <li>✓ 2x2 Picture</li>
                            <li>✓ Birth/Marriage Certificate</li>
                            <li>✓ Latest School/Government ID</li>
                            <li>✓ Copy of Grades</li>
                          </ul>
                        </div>
                      ) : (
                        <div style={{ background: "#fff3cd", border: "2px solid #ffc107", borderRadius: 8, padding: 20, color: "#856404", marginBottom: 24 }}>
                          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>⚠ Missing Requirements</div>
                          <div style={{ marginBottom: 12 }}>
                            Kindly visit <b>Registrar Office</b> and submit your physical copy of the following requirements:
                          </div>
                          <ul style={{ marginTop: 12, marginLeft: 24, lineHeight: 1.8 }}>
                            {requirementsClearance.missingReqs.map((req) => (
                              <li key={req} style={{ color: "#d9534f" }}>
                                ✗ {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Finance Section */}
                    <div style={{ marginBottom: 40 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: "#0f5f3b", borderBottom: "3px solid #0f5f3b", paddingBottom: 8 }}>
                        Financial Status
                      </div>

                      {isFinanceCleared ? (
                        <div style={{ background: "#d4edda", border: "2px solid #28a745", borderRadius: 8, padding: 20, color: "#155601" }}>
                          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>✓ Account Settled</div>
                          <div>Your account balance is fully paid. No outstanding balance.</div>
                          <div style={{ marginTop: 12, fontSize: 18, fontWeight: 700 }}>
                            Balance: ₱ 0.00
                          </div>
                        </div>
                      ) : (
                        <div style={{ background: "#f8d7da", border: "2px solid #f5c6cb", borderRadius: 8, padding: 20, color: "#721c24" }}>
                          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>⚠ Outstanding Balance</div>
                          <div style={{ marginBottom: 12 }}>
                            Kindly visit <b>Finance Office</b> to settle your balance.
                          </div>
                          <div style={{ marginTop: 12, fontSize: 20, fontWeight: 700, color: "#d9534f" }}>
                            Outstanding Balance: ₱ {studentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </div>
                          <div style={{ marginTop: 16, fontSize: 14, background: "#fff", padding: 12, borderRadius: 6, color: "#333" }}>
                            <b>Action Required:</b> Please proceed to the Finance Office to make the necessary payments to clear your account before proceeding with pre-registration.
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Clearance Status */}
                    <div style={{ background: isFullyCleared ? "#e8f5e9" : "#ffebee", borderRadius: 8, padding: 24, border: `2px solid ${isFullyCleared ? "#4caf50" : "#ef5350"}`, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: isFullyCleared ? "#2e7d32" : "#c62828" }}>
                        {isFullyCleared ? "✓ CLEARED FOR PRE-REGISTRATION" : "✗ NOT CLEARED FOR PRE-REGISTRATION"}
                      </div>
                      <div style={{ fontSize: 16, color: isFullyCleared ? "#558b2f" : "#e53935" }}>
                        {isFullyCleared
                          ? "You have completed all requirements and settled your balance. You may now proceed with course line-up."
                          : "Please complete all requirements and settle your balance before proceeding with pre-registration."}
                      </div>

                      {isFullyCleared && (
                        <button
                          onClick={() => navigate("/enrolled/course-lineup", { state: { student } })}
                          style={{
                            marginTop: 24,
                            background: "#0f5f3b",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "14px 48px",
                            fontSize: 16,
                            fontWeight: 700,
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                          }}
                        >
                          PROCEED TO COURSE LINE-UP →
                        </button>
                      )}
                    </div>

                    {/* Info Section */}
                    <div style={{ marginTop: 40, background: "#f5f5f5", borderRadius: 8, padding: 20, fontSize: 14, color: "#555", lineHeight: 1.8 }}>
                      <b>Note:</b> To complete your clearance, ensure that:
                      <ul style={{ marginTop: 12, marginLeft: 24 }}>
                        <li>All physical documents have been submitted to the Registrar Office</li>
                        <li>Your financial account is settled with the Finance Office</li>
                        <li>Your requirements status shows "Submitted" in the system</li>
                      </ul>
                    </div>
                  </div>
                )}

                  {/* COURSE LINE-UP STEP */}
                  {step === "COURSE LINE-UP" && (
                  <div style={{ background: "#fff", borderRadius: 12, padding: "48px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                    <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 32, color: "#0f5f3b", textAlign: "center" }}>
                      COURSE LINE-UP
                    </div>
                    <div style={{ textAlign: "center", color: "#999", padding: "60px 24px" }}>
                      Redirecting to Course Line-up...
                    </div>
                  </div>
                )}

                  {/* PAYMENT OF FEES STEP */}
                  {step === "PAYMENT OF FEES" && (
                  <div style={{ background: "#fff", borderRadius: 12, padding: "48px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                    <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 32, color: "#0f5f3b", textAlign: "center" }}>
                      PAYMENT OF FEES
                    </div>
                    <div style={{ textAlign: "center", color: "#999", padding: "60px 24px" }}>
                      Payment of Fees functionality coming soon...
                    </div>
                  </div>
                )}
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}